<script lang="ts">
  import { onMount } from 'svelte';
  import { lawyerListStore } from '@entities/lawyer/store.svelte';
  import LawyerTable from '@widgets/lawyer-table/LawyerTable.svelte';
  import Pagination from '@shared/ui/Pagination.svelte';
  import { listProducts } from '@shared/api/products';
  import { listAssignableAccounts } from '@shared/api/accounts';
  import { lawyerPositionsApi } from '@shared/api/lawyer-positions';
  import { parsePaged } from '@shared/api/paging';
  import { parseProduct } from '@domain/product/parseProduct';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import {
    isDefaultCustomerStatuses,
    type SimpleOption,
  } from '@shared/constants/choices';
  import type { LawyerListQuery } from '@shared/types/lawyer';
  import { MAX_LIST_VIEW_SEARCH_LENGTH } from '@shared/lib/list-view-state';
  import {
    createListViewPersistence,
    type ListViewPersistence,
  } from '@features/list-view-state/persistence';
  import { withUnknownSelectedOptions } from '@features/list-view-state/unknown-options';
  import {
    createDefaultLawyerListQuery,
    serializeLawyerListQuery,
    validateLawyerListQuery,
  } from './lawyer-list-view-state';

  let query = $state<LawyerListQuery>(createDefaultLawyerListQuery());

  let qInput = $state('');
  let productOptions = $state<SimpleOption[]>([]);
  let positionOptions = $state<SimpleOption[]>([]);
  // 担当者フィルタの選択肢 (PR-ACC-3)。 app_accounts の invited/active を候補にする。
  let accountOptions = $state<SimpleOption[]>([]);
  let persistence: ListViewPersistence | null = null;
  const productFilterOptions = $derived(
    withUnknownSelectedOptions(productOptions, query.product_ids),
  );
  const accountFilterOptions = $derived(
    withUnknownSelectedOptions(accountOptions, query.assignee_account_ids),
  );
  // 役職は役職マスタ (API 由来) の動的候補。 復元した役職がマスタに無い場合でも
  // 生の名称ラベルでチェックボックスを補い、 個別に解除できるようにする。
  const positionFilterOptions = $derived(
    withUnknownSelectedOptions(positionOptions, query.positions, (v) => v),
  );

  const hasActiveFilters = $derived(
    qInput.trim() !== '' ||
      (query.q?.trim() ?? '') !== '' ||
      (query.positions?.length ?? 0) > 0 ||
      (query.legal_training_terms?.length ?? 0) > 0 ||
      (query.bar_associations?.length ?? 0) > 0 ||
      (query.next_action_types?.length ?? 0) > 0 ||
      (query.next_action_buckets?.length ?? 0) > 0 ||
      (query.last_contact_buckets?.length ?? 0) > 0 ||
      (query.product_ids?.length ?? 0) > 0 ||
      (query.assignee_account_ids?.length ?? 0) > 0 ||
      !isDefaultCustomerStatuses(query.sales_statuses),
  );

  async function refresh() {
    await lawyerListStore.refresh(query);
  }

  function applyQuery(next: LawyerListQuery) {
    query = next;
    // スクロールを先頭へ戻し、 適用済み条件を保存する (localStorage + session)。
    persistence?.onQueryApplied();
    void refresh();
  }

  function submitSearch(ev: Event): void {
    ev.preventDefault();
    const q = qInput.trim().slice(0, MAX_LIST_VIEW_SEARCH_LENGTH);
    qInput = q;
    applyQuery({ ...query, q: q || undefined, page: 1 });
  }

  function toggleSort(field: NonNullable<LawyerListQuery['sort_by']>) {
    let next: LawyerListQuery;
    if (query.sort_by === field) {
      if (query.sort_dir === 'asc') {
        next = { ...query, sort_dir: 'desc', page: 1 };
      } else {
        next = { ...query, sort_by: 'updated_at', sort_dir: 'desc', page: 1 };
      }
    } else {
      next = { ...query, sort_by: field, sort_dir: 'asc', page: 1 };
    }
    applyQuery(next);
  }

  function changePage(page: number) {
    query = { ...query, page };
    persistence?.onPageChanged();
    void refresh();
  }

  function clearFilters(): void {
    qInput = '';
    applyQuery({
      ...query,
      q: undefined,
      positions: undefined,
      legal_training_terms: undefined,
      bar_associations: undefined,
      sales_grades: undefined,
      sales_statuses: undefined,
      next_action_types: undefined,
      next_action_buckets: undefined,
      last_contact_buckets: undefined,
      product_ids: undefined,
      assignee_account_ids: undefined,
      page: 1,
    });
  }

  async function loadTableOptions() {
    try {
      const products = parsePaged(
        await listProducts({ page_size: 200 }),
        parseProduct,
      );
      productOptions = products.items.map((p) => ({
        value: p.id,
        label: p.name,
      }));
    } catch {
      productOptions = [];
    }

    try {
      const positions = await lawyerPositionsApi.list();
      positionOptions = positions.items.map((p) => ({
        value: p.name,
        label: p.name,
      }));
    } catch {
      positionOptions = [];
    }

    try {
      const accounts = await listAssignableAccounts();
      accountOptions = accounts
        .filter((a) => a.status === 'invited' || a.status === 'active')
        .map((a) => ({
          value: a.id,
          label: a.display_name,
        }));
    } catch {
      accountOptions = [];
    }
  }

  onMount(() => {
    void loadTableOptions();

    const user = sessionStore.user;
    const p = createListViewPersistence<LawyerListQuery>({
      screen: 'lawyers',
      identity: user ? { userId: user.id, orgId: user.orgId } : null,
      createDefaults: createDefaultLawyerListQuery,
      validate: validateLawyerListQuery,
      serialize: serializeLawyerListQuery,
      getQuery: () => query,
      setQuery: (next) => {
        query = next;
      },
      syncSearchInput: (next) => {
        qInput = next.q ?? '';
      },
      refresh,
      getListState: () =>
        lawyerListStore.data
          ? {
              total: lawyerListStore.data.total,
              page_size: lawyerListStore.data.page_size,
            }
          : null,
      getListError: () => lawyerListStore.error,
    });
    persistence = p;
    void p.initialize();

    return () => {
      persistence = null;
      p.dispose();
    };
  });
</script>

<header class="page-head">
  <div>
    <h1>弁護士</h1>
  </div>
  <form class="search-form" onsubmit={submitSearch}>
    <input
      type="search"
      class="search-box"
      bind:value={qInput}
      maxlength={MAX_LIST_VIEW_SEARCH_LENGTH}
      placeholder="氏名 / ふりがな / 登録番号 / 事務所名で検索"
      aria-label="氏名 / ふりがな / 登録番号 / 事務所名で検索"
    />
  </form>
</header>

<section>
  {#if lawyerListStore.loading && lawyerListStore.data === null}
    <div class="panel"><p class="muted">読み込み中…</p></div>
  {:else if lawyerListStore.error}
    <div class="error">{lawyerListStore.error}</div>
  {:else if lawyerListStore.data}
    <div class="panel">
      <LawyerTable
        lawyers={lawyerListStore.data.items}
        {query}
        productOptions={productFilterOptions}
        positionOptions={positionFilterOptions}
        accountOptions={accountFilterOptions}
        {hasActiveFilters}
        onChange={applyQuery}
        onSort={toggleSort}
        onClearFilters={clearFilters}
      />
      <Pagination
        total={lawyerListStore.data.total}
        page={lawyerListStore.data.page}
        pageSize={lawyerListStore.data.page_size}
        onChange={changePage}
      />
    </div>
  {/if}
</section>

<style>
  .page-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 12px;
    flex-wrap: wrap;
  }
  .page-head h1 {
    margin: 0;
    font-size: 1.25rem;
  }
  .search-form {
    display: flex;
    justify-content: flex-end;
  }
  .search-box {
    width: 420px;
    max-width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    font-size: 13px;
    background: #ffffff;
  }
  .search-box:focus {
    outline: none;
    border-color: #2563eb;
  }
</style>
